import React, {useMemo, useState} from "react";
import {Alert, Container, Spinner} from "react-bootstrap";
import {PiWarningCircle} from "react-icons/pi";
import InventoryStrategyAssumptions from "../../Components/Statistics/InventoryStrategyAssumptions.jsx";
import LagerplaetzeWarehouseSettings from "../../Components/Statistics/LagerplaetzeWarehouseSettings.jsx";
import LagerplaetzeFloorPlan from "../../Components/Statistics/LagerplaetzeFloorPlan.jsx";
import LagerplaetzeStellplatzKacheln from "../../Components/Statistics/LagerplaetzeStellplatzKacheln.jsx";
import LagerplaetzeLegend from "../../Components/Statistics/LagerplaetzeLegend.jsx";
import LagerplaetzeTooltip from "../../Components/Statistics/LagerplaetzeTooltip.jsx";
import {computeRackLayout, deriveSquareSideForCapacity, distributeRowsToWarehouses, palletsNeed, warehouseCapacity} from "../../Components/Statistics/LagerplaetzeLayout.js";
import useStatisticsData from "../../Components/Statistics/useStatisticsData.js";
import {buildGroupColorMap, formatNumber, formatPercent, GROUP_PALETTE} from "../../Components/Statistics/format.js";
import {formatPallets} from "../../Components/Statistics/inventoryStrategyShared.jsx";

// Gemeinsame Regalannahmen aller Hallen, siehe Auftrag: Palettenstell-
// platz 0,8 × 1,2 m, Doppelregal-Arbeitsgang 3,2 m (Schubmaststapler),
// Quergang 4,0 m je Hallenende, 3 Regalebenen. Über
// LagerplaetzeWarehouseSettings je Halle sofort änderbar.
const DEFAULT_RACK_PARAMS = {
    palletWidth: 0.8,
    rackDepth: 1.2,
    aisleWidth: 3.2,
    endAisle: 4.0,
    levels: 3,
};

// Weilheim: 500 Palettenplätze sind vom Auftraggeber bestätigt, die Maße
// dazu nicht - deshalb wird hier die kleinste quadratische Seitenlänge
// gesucht, mit der das Regallayout diese Zielkapazität gerade erreicht
// (siehe Auftrag "leite die Hallenmaße hier aus der Platzzahl ab").
const WEILHEIM_TARGET_CAPACITY = 500;
const WEILHEIM_SIDE = deriveSquareSideForCapacity(WEILHEIM_TARGET_CAPACITY, DEFAULT_RACK_PARAMS);

// Standardwerte: Weilheim quadratisch, Maße aus der bestätigten Platzzahl
// abgeleitet. Die Lahr-Hallen tragen die vermessenen Außenmaße aus Plan #64
// (Baupläne 2006/2007, gegen ALKIS geprüft); ihre Kapazität kommt aus der
// Kapazitätsdatei (siehe mitKapazitaetsdatei), das Regalmodell ist dort nur
// Rückfall. Alle Felder je Halle sind über LagerplaetzeWarehouseSettings
// sofort änderbar.
const DEFAULT_WAREHOUSES = [
    {
        id: "weilheim",
        name: "Weilheim (SNV, Versandlager)",
        length: WEILHEIM_SIDE,
        width: WEILHEIM_SIDE,
        ...DEFAULT_RACK_PARAMS,
        note: `Palettenplätze vom Auftraggeber bestätigt (${WEILHEIM_TARGET_CAPACITY}). Die quadratische Fläche ist daraus zurückgerechnet (kleinste Seitenlänge, mit der das Regallayout unten diese Zielkapazität erreicht) - dazu hat der Auftraggeber keine Maße genannt.`,
    },
    {
        id: "lahr_k28",
        name: "Lahr K28",
        length: 72.6,
        width: 39.47,
        ...DEFAULT_RACK_PARAMS,
        note: "Außenmaße aus den Bauplänen 2007, gegen ALKIS geprüft.",
    },
    {
        id: "lahr_k29",
        name: "Lahr K29",
        length: 55.19,
        width: 9.76,
        ...DEFAULT_RACK_PARAMS,
        note: "Außenmaße aus den Bauplänen 2006/2007, gegen ALKIS geprüft. Überwiegend Fachbodenregale ohne Palettenplätze.",
    },
    {
        id: "lahr_k31",
        name: "Lahr K31",
        length: 38.0,
        width: 11.75,
        ...DEFAULT_RACK_PARAMS,
        fileCapacity: 0,
        note: "Gerader Trakt 38,00 m, Breiten 14,50 m und 11,75 m (gezeichnet mit 11,75 m). Ladengeschäft, Büros und Galerie - keine Palettenregale.",
    },
];

/**
 * Setzt die Kapazität der Lahr-Hallen aus der Kapazitätsdatei (Prop
 * kapazitaetLahr, StatisticsController::lagerplaetze()). Hallen, die in der
 * Datei fehlen, behalten das Regalmodell als Rückfall; fehlt die Datei ganz,
 * bleibt alles beim Modell.
 */
function mitKapazitaetsdatei(warehouses, datei) {
    if (!datei?.standorte) {
        return warehouses;
    }
    const stand = datei.datum
        ? new Date(`${datei.datum}T00:00:00`).toLocaleDateString("de-DE", {day: "2-digit", month: "2-digit", year: "numeric"})
        : null;
    return warehouses.map((warehouse) => {
        const standort = datei.standorte[warehouse.id];
        if (!standort) {
            return warehouse;
        }
        return {
            ...warehouse,
            length: standort.laenge_m ?? warehouse.length,
            width: warehouse.id === "lahr_k31" ? warehouse.width : (standort.breite_m ?? warehouse.width),
            fileCapacity: standort.kapazitaet,
            capacityFile: {
                kapazitaet: standort.kapazitaet,
                regal: standort.regal,
                boden: standort.boden,
                reihen: standort.reihen,
                stand,
                hoehenklassen: Object.entries(datei.hoehenklassen || {})
                    .map(([key, label]) => ({label, plaetze: standort.hoehenklassen?.[key] || 0}))
                    .filter(({plaetze}) => plaetze > 0),
                zonen: standort.zonen || [],
            },
        };
    });
}

// Breite der längsten Halle im SVG, in Pixeln - daraus ergibt sich ein für
// alle Hallen identischer Maßstab (Meter je Pixel), siehe Auftrag
// Punkt 1 ("massstabsgetreu zueinander").
const MAX_DISPLAY_WIDTH_PX = 720;

/**
 * Lagerplätze: zeigt die Hallen massstabsgetreu zueinander als echtes
 * Regallayout - Regalzeilen längs der Halle, dazwischen Arbeitsgänge für den
 * Stapler, an beiden Enden Quergänge (siehe computeRackLayout()/
 * layoutRackRows() in LagerplaetzeLayout.js). Darin sind die Sortimente
 * abschnittsweise in die Regalreihen einsortiert, flächenproportional zum
 * empfohlenen Palettenbedarf. Grundlage der Palettenbedarfe ist derselbe
 * Endpunkt wie die Lagerstrategie-Detailtabelle
 * (/statistics/inventory-strategy/flaechenbedarf), gesteuert über dieselben
 * sechs Rechenannahmen (InventoryStrategyAssumptions, unverändert
 * wiederverwendet). Die Verteilung auf die Hallen selbst ist dagegen
 * eine Annahme dieser Oberfläche, keine Vorgabe des Auftraggebers - siehe
 * Erläuterung im Text unterhalb der Kennzahlen.
 */
export default function Lagerplaetze({kapazitaetLahr = null}) {
    const [assumptionParams, setAssumptionParams] = useState(null);
    const [warehouses, setWarehouses] = useState(() => mitKapazitaetsdatei(DEFAULT_WAREHOUSES, kapazitaetLahr));
    const [hover, setHover] = useState(null);

    const {data, loading, error} = useStatisticsData(
        "/statistics/inventory-strategy/flaechenbedarf",
        assumptionParams || {},
        {skip: !assumptionParams}
    );

    // Nur Sortimente mit bekannter Palettengröße und tatsächlichem Bedarf
    // lassen sich als Fläche darstellen - der Rest wird unten als Zahl
    // ausgewiesen (siehe unassignableCount).
    const rows = useMemo(
        () => (data?.rows || []).filter((row) => palletsNeed(row) > 0),
        [data]
    );
    const unassignableCount = data?.total?.sortiment_count_unassignable || 0;

    // Feste Farbzuordnung je Warengruppe, unabhängig von der Verteilung auf
    // die Hallen - sonst würde ein Wechsel der Rechenannahmen die Farben
    // verschieben (siehe Regel in format.js).
    const groupOrder = useMemo(() => {
        const totals = new Map();
        rows.forEach((row) => {
            if (!row.article_group) {
                return;
            }
            totals.set(row.article_group, (totals.get(row.article_group) || 0) + palletsNeed(row));
        });
        return Array.from(totals.entries())
            .sort((a, b) => b[1] - a[1])
            .map(([name]) => name);
    }, [rows]);
    const colorMap = useMemo(() => buildGroupColorMap(groupOrder), [groupOrder]);
    const groupsPresent = groupOrder.slice(0, GROUP_PALETTE.length);
    const hasOther = rows.some((row) => !colorMap[row.article_group]);

    // Kapazität je Halle: gezählt laut Kapazitätsdatei (Lahr), sonst aus dem
    // Regallayout (siehe warehouseCapacity) - distributeRowsToWarehouses()
    // braucht dafür ein Feld `capacity` je Halle.
    const warehousesWithCapacity = useMemo(
        () => warehouses.map((warehouse) => ({...warehouse, capacity: warehouseCapacity(warehouse)})),
        [warehouses]
    );

    const {assignments, overflow} = useMemo(
        () => distributeRowsToWarehouses(rows, warehousesWithCapacity),
        [rows, warehousesWithCapacity]
    );

    const totalDemand = data?.total?.pallets_target_full ?? rows.reduce((sum, row) => sum + palletsNeed(row), 0);
    const totalCapacity = warehousesWithCapacity.reduce((sum, warehouse) => sum + warehouse.capacity, 0);
    const overflowPallets = overflow.reduce((sum, row) => sum + palletsNeed(row), 0);

    // Verkehrsflächenquote über alle Hallen zusammen - die Zahl, die in
    // der bisherigen Kachel-Darstellung gefehlt hat (siehe Auftrag).
    const overallLayouts = useMemo(() => warehouses.map((warehouse) => computeRackLayout(warehouse)), [warehouses]);
    const overallArea = overallLayouts.reduce((sum, layout) => sum + layout.totalArea, 0);
    const overallTrafficArea = overallLayouts.reduce((sum, layout) => sum + layout.trafficArea, 0);
    const overallTrafficPercent = overallArea > 0 ? (overallTrafficArea / overallArea) * 100 : 0;

    const handleFieldChange = (id, field, value) => {
        setWarehouses((prev) => prev.map((warehouse) => (warehouse.id === id ? {...warehouse, [field]: value} : warehouse)));
    };

    const maxLength = Math.max(...warehouses.map((warehouse) => warehouse.length), 1);
    const pxPerMeter = MAX_DISPLAY_WIDTH_PX / maxLength;

    return (
        <Container fluid className="pt-2 pb-4">
            <h1 className="h4 mb-3">Lagerplätze</h1>

            {/*
              * Der Stellplatzbedarf steht bewusst ganz oben und vor allem, was
              * mit den Hallen zu tun hat: Er folgt aus den echten Absatzdaten
              * und bleibt gültig, egal wie die Hallen am Ende vermessen werden.
              * Die Hallendarstellungen darunter beruhen dagegen noch auf
              * Annahmen (siehe Warnhinweis).
              */}
            {!assumptionParams || loading ? (
                <div className="d-flex justify-content-center py-4">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Wird geladen…</span>
                    </Spinner>
                </div>
            ) : (
                <LagerplaetzeStellplatzKacheln
                    rows={data?.rows || []}
                    total={data?.total}
                    colorMap={colorMap}
                    unassignableCount={unassignableCount}
                    assumptionParams={assumptionParams}
                />
            )}

            {/* Rechenannahmen gehören direkt unter die Kacheln - sie sind es,
                die deren Zahlen verändern. */}
            <div className="mb-4">
                <InventoryStrategyAssumptions onChangeParams={setAssumptionParams} />
            </div>

            <h2 className="h5 mb-2">Verteilung auf die Hallen</h2>

            <Alert variant="warning" className="d-flex align-items-start gap-2">
                <PiWarningCircle size={20} className="flex-shrink-0 mt-1" aria-hidden="true" />
                <div className="small">
                    <strong>Zur Belastbarkeit dieser Ansicht:</strong> Die 500 Palettenplätze der Halle Weilheim sind vom
                    Auftraggeber bestätigt, ihre Maße sind daraus zurückgerechnet und damit selbst eine Annahme. Die
                    Lahr-Hallen K28, K29 und K31 tragen die vermessenen Außenmaße aus den Bauplänen 2006/2007.
                    {kapazitaetLahr ? (
                        <> Ihre Palettenplätze ({formatNumber(kapazitaetLahr.kapazitaet)}) sind aus dem Regalplan 2025
                            gezählt (Kapazitätsdatei Lahr, Stand{" "}
                            {kapazitaetLahr.datum ? new Date(`${kapazitaetLahr.datum}T00:00:00`).toLocaleDateString("de-DE") : "unbekannt"}).
                            {kapazitaetLahr.messstand_hinweis ? <> {kapazitaetLahr.messstand_hinweis}</> : null} In Lahr
                            ist die Zeichnung deshalb nur schematisch. Weilheim rechnet weiter mit dem Regallayout
                            (Regalzeilen, Arbeitsgänge, Quergänge), dessen Gänge, Ebenen und Wareneingangszone selbst
                            Annahmen sind.</>
                    ) : (
                        <> Die Kapazitätsdatei Lahr liegt nicht vor; alle Hallen rechnen deshalb mit dem Regallayout
                            (Regalzeilen, Arbeitsgänge, Quergänge), dessen Gänge, Ebenen und Wareneingangszone selbst
                            Annahmen sind.</>
                    )}{" "}
                    Die aktuell belegten Paletten stammen ausschließlich aus dem Lager Weilheim - Lahr wird derzeit
                    erst erfasst und fließt deshalb nirgends als „aktuell belegt" ein. Maße und Regalannahmen sind
                    über „Hallenmaße und Regalannahmen" unten sofort änderbar.
                </div>
            </Alert>

            <div className="d-flex flex-column gap-3 mb-3">
                <LagerplaetzeWarehouseSettings warehouses={warehouses} onChangeField={handleFieldChange} />
            </div>

            {error && <Alert variant="danger">{error}</Alert>}

            {!assumptionParams || loading ? (
                <div className="d-flex justify-content-center py-5">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Wird geladen…</span>
                    </Spinner>
                </div>
            ) : (
                <>
                    <div className="mb-3 small text-muted">
                        Gesamtbedarf {formatNumber(totalDemand)} Paletten (auf volle Paletten gerundet, wie in den Kacheln oben)
                        von insgesamt {formatNumber(totalCapacity)} Palettenplätzen über alle Hallen ({formatPercent(totalCapacity > 0 ? (totalDemand / totalCapacity) * 100 : 0, 0)}
                        ). Laut Regalmodell sind {formatPercent(overallTrafficPercent, 0)} der Gesamtfläche ({formatNumber(Math.round(overallTrafficArea))} m² von{" "}
                        {formatNumber(Math.round(overallArea))} m²) Verkehrsfläche - Arbeitsgänge, Quergänge und Randabstand, keine
                        Stellfläche. Zuordnungsregel dieser Oberfläche (keine betriebliche Vorgabe): die Sortimente mit
                        dem größten empfohlenen Palettenbedarf füllen zuerst Weilheim als Versandlager bis zu dessen
                        Kapazität, danach kaskadierend K28 und anschließend K29 (K31 hat keine Palettenplätze) - ein einmal
                        übersprungenes Sortiment füllt keine später frei bleibende Lücke einer vorherigen Halle mehr
                        auf.
                        {unassignableCount > 0 && (
                            <> {unassignableCount} weitere Sortimente ohne bekannte Palettengröße sind hier nicht darstellbar.</>
                        )}
                    </div>

                    {overflow.length > 0 && (
                        <Alert variant="danger" className="d-flex align-items-start gap-2">
                            <PiWarningCircle size={20} className="flex-shrink-0 mt-1" aria-hidden="true" />
                            <div className="small">
                                <strong>Kapazität reicht nicht aus:</strong> {overflow.length} Sortimente mit zusammen{" "}
                                {formatPallets(overflowPallets)} finden in keiner der Hallen mehr Platz.
                            </div>
                        </Alert>
                    )}

                    <LagerplaetzeLegend colorMap={colorMap} groupsPresent={groupsPresent} hasOther={hasOther} />

                    <div className="d-flex flex-wrap gap-4 mt-3">
                        {assignments.map(({warehouse, rows: hallRows, assignedPallets}) => (
                            <LagerplaetzeFloorPlan
                                key={warehouse.id}
                                warehouse={warehouse}
                                assignedRows={hallRows}
                                assignedPallets={assignedPallets}
                                colorMap={colorMap}
                                pxPerMeter={pxPerMeter}
                                onHoverBlock={(row, event, hallWarehouse, capacity) =>
                                    setHover({
                                        row,
                                        warehouse: hallWarehouse,
                                        capacityPallets: capacity,
                                        x: event.clientX,
                                        y: event.clientY,
                                    })
                                }
                                onLeaveBlock={() => setHover(null)}
                            />
                        ))}
                    </div>
                </>
            )}

            <LagerplaetzeTooltip hover={hover} onClose={() => setHover(null)} />
        </Container>
    );
}
